import { useState, useEffect } from 'react';
import { Trophy, Globe, Building, Medal } from 'lucide-react';
import { fetchLeaderboard, type LeaderboardEntry } from '@/lib/api';
import { ResponsiveTable, type Column } from '@/components/ui/ResponsiveTable';
import { useRecentRoute } from '@/hooks/useRecentRoute';
import styles from './LeaderboardPage.module.css';

const RANK_COLORS: Record<string, string> = {
  bronze: '#cd7f32',
  silver: '#c0c0c0',
  gold: '#ffd700',
  platinum: '#e5e4e2',
  diamond: '#b9f2ff',
};

function getMedalColor(position: number): string {
  if (position === 1) return '#ffd700';
  if (position === 2) return '#c0c0c0';
  if (position === 3) return '#cd7f32';
  return 'var(--color-tx-m)';
}

export function LeaderboardPage() {
  useRecentRoute('Leaderboard');
  const [scope, setScope] = useState<'global' | 'tenant'>('global');
  const [entries, setEntries] = useState<LeaderboardEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    setLoading(true);
    setError('');
    fetchLeaderboard(scope, 100)
      .then(setEntries)
      .catch((err) => setError(err instanceof Error ? err.message : 'Failed to load'))
      .finally(() => setLoading(false));
  }, [scope]);

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <div className={styles.headerIcon}><Trophy size={18} /></div>
          <div>
            <h1 className={styles.title}>Leaderboard</h1>
            <p className={styles.subtitle}>Top drivers by points</p>
          </div>
        </div>
      </div>

      <div className={styles.tabs}>
        <button
          className={`${styles.tab} ${scope === 'global' ? styles.tabActive : ''}`}
          onClick={() => setScope('global')}
        >
          <Globe size={14} /> Global
        </button>
        <button
          className={`${styles.tab} ${scope === 'tenant' ? styles.tabActive : ''}`}
          onClick={() => setScope('tenant')}
        >
          <Building size={14} /> Tenant
        </button>
      </div>

      {error && <div className={styles.error}>{error}</div>}

      {loading ? (
        <div className={styles.loading}>Loading...</div>
      ) : entries.length === 0 ? (
        <div className={styles.empty}>
          <Trophy size={32} className={styles.emptyIcon} />
          <p>No leaderboard entries yet — leaderboards are generated after sessions complete.</p>
        </div>
      ) : (
        <div className={styles.list}>
          {/* Top 3 podium */}
          {entries.length >= 3 && (
            <div className={styles.podium}>
              {[entries[1], entries[0], entries[2]].map((e, idx) => {
                if (!e) return null;
                const podiumPos = idx === 1 ? 1 : (idx === 0 ? 2 : 3);
                return (
                  <div key={e.user_id} className={`${styles.podiumCard} ${styles[`podium${podiumPos}`]}`}>
                    <Medal size={20} style={{ color: getMedalColor(podiumPos) }} />
                    <div className={styles.podiumPosition}>#{podiumPos}</div>
                    {e.avatar_url ? (
                      <img src={e.avatar_url} alt="" className={styles.podiumAvatar} />
                    ) : (
                      <div className={styles.podiumAvatarPlaceholder}>?</div>
                    )}
                    <div className={styles.podiumName}>{e.username ?? 'Anonymous'}</div>
                    {e.current_rank && (
                      <div className={styles.podiumRank} style={{ color: RANK_COLORS[e.current_rank?.toLowerCase()] ?? 'var(--color-tx-m)' }}>
                        {e.current_rank}
                      </div>
                    )}
                    <div className={styles.podiumPoints}>{e.points.toLocaleString()} pts</div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Rest of the table — ResponsiveTable stacks rows into cards
              on mobile (<640px) so rank/user/points stay glanceable at
              phone width, falls back to the full 6-col grid on desktop. */}
          <LeaderboardTable entries={entries} />
        </div>
      )}
    </div>
  );
}

function LeaderboardTable({ entries }: { entries: LeaderboardEntry[] }) {
  const columns: Column<LeaderboardEntry>[] = [
    {
      key: 'rank',
      header: 'Rank',
      mobile: 'hidden',
      cell: (e) => (
        <span
          className={styles.position}
          style={{
            color: getMedalColor(e.position),
            fontWeight: e.position <= 3 ? 700 : 500,
          }}
        >
          #{e.position}
        </span>
      ),
    },
    {
      key: 'user',
      header: 'User',
      mobile: 'primary',
      cell: (e) => (
        <div className={styles.userCell}>
          {e.avatar_url ? (
            <img src={e.avatar_url} alt="" className={styles.smallAvatar} />
          ) : (
            <div className={styles.smallAvatarPlaceholder}>?</div>
          )}
          <span className={styles.userCellName}>
            <span className={styles.userCellPosition}>#{e.position}</span>
            {e.username ?? <em className={styles.dim}>anonymous</em>}
          </span>
        </div>
      ),
    },
    {
      key: 'tier',
      header: 'Tier',
      mobile: 'secondary',
      cell: (e) => (
        e.current_rank ? (
          <span
            className={styles.rankBadge}
            style={{ color: RANK_COLORS[e.current_rank?.toLowerCase()] ?? 'var(--color-tx-m)' }}
          >
            {e.current_rank}
          </span>
        ) : (
          <span className={styles.dim}>—</span>
        )
      ),
    },
    {
      key: 'sessions',
      header: 'Sessions',
      mobile: 'body',
      align: 'right',
      cell: (e) => e.total_sessions,
    },
    {
      key: 'kwh',
      header: 'kWh',
      mobile: 'body',
      align: 'right',
      cell: (e) => e.total_kwh.toFixed(1),
    },
    {
      key: 'points',
      header: 'Points',
      mobile: 'body',
      align: 'right',
      cell: (e) => (
        <span className={styles.pointsCell}>{e.points.toLocaleString()}</span>
      ),
    },
  ];

  return (
    <ResponsiveTable
      columns={columns}
      rows={entries}
      rowKey={(e) => e.user_id}
      emptyMessage="No leaderboard entries yet"
    />
  );
}

